// File: ResumeBuilder.js
import React, { useState, useEffect } from 'react';
import { Container, Grid } from '@mui/material';
import html2pdf from 'html2pdf.js';
import FormSection from '../components/FormSection';
import PreviewSection from '../components/PreviewSection';  // Import PreviewSection component

const ResumeBuilder = () => {
    // Calculate viewport height for container
    const [viewportHeight, setViewportHeight] = useState('100vh');

    useEffect(() => {
        // Set initial height and update on resize
        const updateHeight = () => {
            setViewportHeight(`${window.innerHeight}px`);
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);

        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    const [resumeData, setResumeData] = useState({
        personalInfo: {
            name: '',
            title: '',
            email: '',
            phone: '',
            location: '',
            linkedIn: '',
            website: ''
        },
        summary: '',
        experience: [
            {
                company: '',
                position: '',
                startDate: '',
                endDate: '',
                description: '',
                achievements: ['']
            }
        ],
        projects: [
            {
                title: '',
                role: '',
                startDate: '',
                endDate: '',
                description: '',
                technologies: '',
                url: '',
                highlights: ['']
            }
        ],
        education: [
            {
                institution: '',
                degree: '',
                field: '',
                graduationDate: '',
                gpa: ''
            }
        ],
        skills: ['']
    });

    // Handle text field changes
    const handleChange = (section, field, value) => {
        if (section === 'summary') {
            setResumeData({ ...resumeData, summary: value });
        } else {
            setResumeData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        }
    };

    // Handle changes to array items (experience, education, skills)
    const handleArrayChange = (section, index, field, value) => {
        setResumeData(prev => ({
            ...prev,
            [section]: prev[section].map((item, i) => {
                if (i !== index) return item;

                // Special case for skills which are strings, not objects
                if (section === 'skills') {
                    return value;
                }

                // For objects (experience, education, projects)
                return { ...item, [field]: value };
            })
        }));
    };

    // Handle changes to nested arrays (achievements within experience, highlights within projects)
    const handleNestedArrayChange = (section, parentIndex, nestedField, nestedIndex, value) => {
        setResumeData(prev => ({
            ...prev,
            [section]: prev[section].map((item, i) =>
                i === parentIndex ? {
                    ...item,
                    [nestedField]: item[nestedField].map((nestedItem, j) =>
                        j === nestedIndex ? value : nestedItem
                    )
                } : item
            )
        }));
    };

    // Add new item to arrays
    const handleAddItem = (section) => {
        if (section === 'experience') {
            setResumeData(prev => ({
                ...prev,
                experience: [...prev.experience, {
                    company: '',
                    position: '',
                    startDate: '',
                    endDate: '',
                    description: '',
                    achievements: ['']
                }]
            }));
        } else if (section === 'projects') {
            setResumeData(prev => ({
                ...prev,
                projects: [...prev.projects, {
                    title: '',
                    role: '',
                    startDate: '',
                    endDate: '',
                    description: '',
                    technologies: '',
                    url: '',
                    highlights: ['']
                }]
            }));
        } else if (section === 'education') {
            setResumeData(prev => ({
                ...prev,
                education: [...prev.education, {
                    institution: '',
                    degree: '',
                    field: '',
                    graduationDate: '',
                    gpa: ''
                }]
            }));
        } else if (section === 'skills') {
            setResumeData(prev => ({
                ...prev,
                skills: [...prev.skills, '']
            }));
        }
    };

    // Add achievement to experience item
    const handleAddAchievement = (experienceIndex) => {
        setResumeData(prev => ({
            ...prev,
            experience: prev.experience.map((exp, i) =>
                i === experienceIndex ? {
                    ...exp,
                    achievements: [...exp.achievements, '']
                } : exp
            )
        }));
    };

    // Add highlight to project item
    const handleAddHighlight = (projectIndex) => {
        setResumeData(prev => ({
            ...prev,
            projects: prev.projects.map((proj, i) =>
                i === projectIndex ? {
                    ...proj,
                    highlights: [...proj.highlights, '']
                } : proj
            )
        }));
    };

    // Remove item from arrays
    const handleRemoveItem = (section, index) => {
        setResumeData(prev => ({
            ...prev,
            [section]: prev[section].filter((_, i) => i !== index)
        }));
    };

    // Remove achievement from experience item
    const handleRemoveAchievement = (experienceIndex, achievementIndex) => {
        setResumeData(prev => ({
            ...prev,
            experience: prev.experience.map((exp, i) =>
                i === experienceIndex ? {
                    ...exp,
                    achievements: exp.achievements.filter((_, j) => j !== achievementIndex)
                } : exp
            )
        }));
    };

    // Remove highlight from project item
    const handleRemoveHighlight = (projectIndex, highlightIndex) => {
        setResumeData(prev => ({
            ...prev,
            projects: prev.projects.map((proj, i) =>
                i === projectIndex ? {
                    ...proj,
                    highlights: proj.highlights.filter((_, j) => j !== highlightIndex)
                } : proj
            )
        }));
    };

    const handlePrint = () => {
        window.print();
    };

    const handleDownloadPDF = () => {
        // Get the resume preview element
        const element = document.getElementById('resume-preview');

        // Set options for the PDF
        const opt = {
            margin: [0, 0, 0, 0],
            filename: `${resumeData.personalInfo.name || 'resume'}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
                scale: 2, // Higher scale for better quality
                useCORS: true,
                logging: false
            },
            jsPDF: {
                unit: 'mm',
                format: 'a4',
                orientation: 'portrait'
            }
        };

        // Generate and download the PDF
        html2pdf()
            .set(opt)
            .from(element)
            .save()
            .catch(err => console.error('Error generating PDF:', err));
    };

    return (
        <Container
            maxWidth="xl"
            sx={{
                height: viewportHeight,
                py: 2,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Grid
                container
                spacing={3}
                sx={{
                    flexGrow: 1,
                    height: '100%'
                }}
            >
                {/* Form Section - Using the separated component */}
                <FormSection
                    resumeData={resumeData}
                    handleChange={handleChange}
                    handleArrayChange={handleArrayChange}
                    handleNestedArrayChange={handleNestedArrayChange}
                    handleAddItem={handleAddItem}
                    handleRemoveItem={handleRemoveItem}
                    handleAddAchievement={handleAddAchievement}
                    handleRemoveAchievement={handleRemoveAchievement}
                    handleAddHighlight={handleAddHighlight}
                    handleRemoveHighlight={handleRemoveHighlight}
                    handlePrint={handlePrint}
                    handleDownloadPDF={handleDownloadPDF}
                />

                {/* Preview Section - Using the separated component */}
                <PreviewSection resumeData={resumeData} />
            </Grid>
        </Container>
    );
};

export default ResumeBuilder;